import type { EidsUserSource } from "@omni/ads";
import type { HashCache } from "../types";

import { fetchPPID3Hashes } from "..";
import { UseLocalStorage } from "../../../hooks/useLocalStorage/useLocalStorage";

const LOCAL_STORAGE_KEY = "hasher_ppid3";

const PPID3CacheHandler = async (
  userIdFromSPiD?: string,
  pulseEnvironmentId?: string,
  isEconomy?: boolean,
): Promise<EidsUserSource[]> => {
  const useLocalStorage = UseLocalStorage();
  const useSessionStorage = UseLocalStorage("sessionStorage");

  const PPPID3Eids: EidsUserSource[] = [];
  const environmentId = pulseEnvironmentId
    ? `sdrn:schibsted:environment:${pulseEnvironmentId}`
    : undefined;
  const clientId = isEconomy ? "omniekonomi" : "omni";
  const trackerType = "Custom";
  const trackerVersion = "1.0.0";
  let userId: string | undefined =
    userIdFromSPiD || useSessionStorage.get("ppid3_user_id") || undefined;

  if (userIdFromSPiD) {
    userId = `sdrn:schibsted.com:user:${userIdFromSPiD}`;
    useSessionStorage.set("ppid3_user_id", userId);
  }

  const fetchAndStoreInCache = async () => {
    const data = await fetchPPID3Hashes({
      clientId,
      userId,
      environmentId,
      trackerType,
      trackerVersion,
    });

    const hashCache: HashCache = {
      userId: {
        plain: userId || null,
        hashed: data?.userId,
      },
      environmentId: {
        plain: environmentId || null,
        hashed: data?.environmentId,
      },
      clientId,
      trackerType,
      trackerVersion,
    };
    useLocalStorage.set(LOCAL_STORAGE_KEY, hashCache);

    if (userId && data?.userId) {
      PPPID3Eids.push({
        id: data.userId,
        source: "schse-userhash-v3",
      });
    }
    if (environmentId && data?.environmentId) {
      PPPID3Eids.push({
        id: data.environmentId,
        source: "schse-envhash-v3",
      });
    }
  };

  try {
    const hashedValue = useLocalStorage.get(
      LOCAL_STORAGE_KEY,
    ) as HashCache | null;

    const environmentOrUserIdIsCachedWithNotSameValues = Boolean(
      hashedValue &&
        ((environmentId && hashedValue.environmentId.plain !== environmentId) ||
          (userId && hashedValue.userId.plain !== userId)),
    );

    const environmentAndUserIdIsCachedButNotSameValues = Boolean(
      hashedValue &&
        ((userId &&
          environmentId &&
          hashedValue.environmentId.plain !== environmentId) ||
          hashedValue.userId.plain !== userId),
    );

    if (
      !hashedValue ||
      environmentOrUserIdIsCachedWithNotSameValues ||
      environmentAndUserIdIsCachedButNotSameValues
    ) {
      await fetchAndStoreInCache();
    } else {
      if (hashedValue) {
        if (userId && hashedValue.userId.hashed) {
          PPPID3Eids.push({
            id: hashedValue.userId.hashed,
            source: "schse-userhash-v3",
          });
        }
        if (environmentId && hashedValue.environmentId.hashed) {
          PPPID3Eids.push({
            id: hashedValue.environmentId.hashed,
            source: "schse-envhash-v3",
          });
        }
      }
    }
  } catch (e) {
    console.error(e);
  }

  return PPPID3Eids;
};

export { PPID3CacheHandler };
